import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail, MailOpen, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { saas, errMsg } from "@/lib/saasApi";

export default function AdminMessages() {
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    saas.adminMessages().then(({ data }) => { setRows(data.messages); setUnread(data.unread); }).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try { await saas.adminMessageRead(id); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-messages">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-re-charcoal">Messages</h1>
        {unread > 0 && <Badge className="bg-re-red text-white">{unread} unread</Badge>}
      </div>
      <p className="rounded-xl bg-re-soft px-4 py-3 text-sm text-re-muted">Contact-form submissions are stored here. Email delivery is not configured yet — reply to customers directly from your own inbox.</p>

      {loading ? <p className="text-sm text-re-muted">Loading…</p> : rows.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-re-line bg-white p-10 text-center text-sm text-re-muted" data-testid="messages-empty">No messages yet.</p>
      ) : (
        <div className="space-y-3" data-testid="messages-list">
          {rows.map((m) => (
            <div key={m.id} className={`rounded-2xl border bg-white p-5 ${m.read ? "border-re-line" : "border-re-red/40"}`} data-testid={`message-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {m.read ? <MailOpen className="h-4 w-4 text-re-muted" /> : <Mail className="h-4 w-4 text-re-red" />}
                    <p className="font-medium text-re-charcoal">{m.firstName} {m.lastName}</p>
                    {m.topic && <Badge variant="secondary">{m.topic}</Badge>}
                  </div>
                  <p className="mt-1 text-xs text-re-muted">{m.email}{m.company ? ` · ${m.company}` : ""}{m.phone ? ` · ${m.phone}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-re-muted">{new Date(m.createdAt).toLocaleString("en-GB")}</span>
                  {!m.read && <Button size="sm" variant="outline" className="rounded-full" data-testid={`mark-read-${m.id}`} onClick={() => markRead(m.id)}><Check className="mr-1 h-3.5 w-3.5" /> Mark read</Button>}
                </div>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm text-re-charcoal">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
